// Типизированный клиент для /api/hypotheses (бэкенд hypotheses.ts).

import { api } from "./client.js";

export type HypothesisStatus =
  | "draft"
  | "pending_review"
  | "approved"
  | "active"
  | "paused"
  | "verdict_pending"
  | "killed"
  | "scaled";

export type HypothesisVerdict = "pass" | "fail" | "inconclusive";

export interface AudienceSegment {
  gender?: "m" | "f";
  age_intervals?: string[];
  region_ids?: number[];
  device_categories?: ("desktop" | "mobile" | "tablet")[];
  interests?: string[];
  bid_modifiers?: BidModifierSpec;
}

export interface BidModifierSpec {
  demographics?: Array<{
    age?: "AGE_18_24" | "AGE_25_34" | "AGE_35_44" | "AGE_45" | "AGE_55";
    gender?: "GENDER_FEMALE" | "GENDER_MALE";
    bid_modifier: number;
  }>;
  mobile_modifier?: number;
}

export interface Hypothesis {
  id: number;
  name: string;
  status: HypothesisStatus;
  product_id: string | null;
  audience_segment: AudienceSegment | null;
  hook_text: string | null;
  ad_body: string | null;
  draft_keywords: string[] | null;
  landing_url: string | null;
  direct_campaign_id: number | null;
  direct_ad_group_id: number | null;
  direct_ad_ids: number[] | null;
  direct_keyword_ids: number[] | null;
  daily_budget_rub: number;
  expected_clicks_to_verdict: number;
  expected_days_to_verdict: number;
  target_roas: number | null;
  verdict_at: string | null;
  verdict: HypothesisVerdict | null;
  verdict_actual_roas: number | null;
  verdict_clicks: number | null;
  verdict_days: number | null;
  verdict_notes: string | null;
  created_at: string;
  approved_at: string | null;
  launched_at: string | null;
  killed_at: string | null;
  persona_label: string | null;
  wave_id: string | null;
}

export interface CreateHypothesisInput {
  name: string;
  daily_budget_rub: number;
  product_id?: string | null;
  audience_segment?: AudienceSegment | null;
  hook_text?: string | null;
  ad_body?: string | null;
  draft_keywords?: string[] | null;
  landing_url?: string | null;
  expected_clicks_to_verdict?: number;
  expected_days_to_verdict?: number;
  target_roas?: number | null;
}

export type UpdateHypothesisInput = Partial<CreateHypothesisInput>;

export interface ListHypothesesResponse {
  entries: Hypothesis[];
  count: number;
}

export interface PauseAllResponse {
  dryRun: boolean;
  total: number;
  ok: number;
  failed: number;
  results: Array<{
    hypothesis_id: number;
    direct_campaign_id: number | null;
    suspended: boolean;
    paused: boolean;
    action_log_id: number | null;
    error?: string;
  }>;
}

export interface LaunchResponse {
  dryRun: boolean;
  hypothesis?: Hypothesis;
  artifacts?: {
    campaign_id: number;
    ad_group_id: number;
    ad_ids: number[];
    keyword_ids: number[];
  };
  steps?: Record<string, unknown>;
  note?: string;
}

export interface LaunchError {
  error: "launch_failed" | "direct_api_error" | "pre_flight_blocked" | "missing_required_fields" | "must_be_approved" | "not_found" | "invalid_id";
  step?: string;
  message?: string;
  missing?: string[];
  current_status?: HypothesisStatus;
  api_error?: { code: number; message: string; detail: string };
  issues?: Array<{ rule: string; severity: string; message: string }>;
  partial?: { campaign_id?: number; ad_group_id?: number; ad_ids?: number[] };
  action_log_id?: number;
}

// ─── CRUD ────────────────────────────────────────────────────────────────

export async function listHypotheses(
  params: { status?: HypothesisStatus; product_id?: string; wave_id?: string; persona_label?: string; limit?: number } = {},
) {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.product_id) qs.set("product_id", params.product_id);
  if (params.wave_id) qs.set("wave_id", params.wave_id);
  if (params.persona_label) qs.set("persona_label", params.persona_label);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api<ListHypothesesResponse>(`/api/hypotheses${suffix}`);
}

// ─── Factorial wave helpers ─────────────────────────────────────────────

export interface FactorialMatrixOptions {
  wave_id: string;
  daily_budget_rub: number;
  expected_clicks_to_verdict?: number;
  expected_days_to_verdict?: number;
  target_roas?: number;
  category_slugs?: string[];
  persona_slugs?: string[];
  geo_slugs?: string[];
}

export async function generateFactorialWave(opts: FactorialMatrixOptions & { dryRun?: boolean }) {
  return api<{
    dryRun: boolean;
    total_cells?: number;
    daily_budget_total_rub: number;
    drafts: Hypothesis[] | unknown[];
    axes?: unknown;
    wave_id?: string;
    created_count?: number;
    skipped_count?: number;
  }>("/api/hypotheses/factorial-wave", {
    method: "POST",
    body: JSON.stringify(opts),
  });
}

export async function massLaunch(opts: { wave_id?: string; hypothesis_ids?: number[]; dryRun?: boolean; auto_approve?: boolean }) {
  return api<{
    dryRun: boolean;
    total: number;
    ok: number;
    failed: number;
    results: Array<{
      hypothesis_id: number;
      status_before: string;
      launched: boolean;
      action_log_id: number | null;
      error?: string;
    }>;
  }>("/api/hypotheses/mass-launch", {
    method: "POST",
    body: JSON.stringify(opts),
  });
}

export async function getHypothesisById(id: number) {
  return api<Hypothesis>(`/api/hypotheses/${id}`);
}

export async function createHypothesis(input: CreateHypothesisInput) {
  return api<Hypothesis>("/api/hypotheses", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateHypothesis(id: number, input: UpdateHypothesisInput) {
  return api<Hypothesis>(`/api/hypotheses/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function deleteHypothesis(id: number) {
  return api<{ deleted: boolean; id: number }>(`/api/hypotheses/${id}`, {
    method: "DELETE",
  });
}

export async function transitionHypothesis(id: number, to: HypothesisStatus) {
  return api<Hypothesis>(`/api/hypotheses/${id}/transition`, {
    method: "POST",
    body: JSON.stringify({ to }),
  });
}

export async function recordVerdict(
  id: number,
  v: { verdict: HypothesisVerdict; actual_roas: number | null; clicks: number | null; days: number | null; notes?: string },
) {
  return api<Hypothesis>(`/api/hypotheses/${id}/verdict`, {
    method: "POST",
    body: JSON.stringify(v),
  });
}

export async function launchHypothesis(id: number, opts: { dryRun?: boolean } = {}) {
  return api<LaunchResponse>(`/api/hypotheses/${id}/launch`, {
    method: "POST",
    body: JSON.stringify(opts),
  });
}

export async function pauseAllHypotheses(opts: { dryRun?: boolean; reason?: string } = {}) {
  return api<PauseAllResponse>("/api/hypotheses/pause-all", {
    method: "POST",
    body: JSON.stringify(opts),
  });
}

// ─── Pre-flight (для wizard live-validation) ─────────────────────────────

export interface PreflightResponse {
  text: string;
  blocked: boolean;
  issues: Array<{ rule: string; severity: "block" | "warn"; message: string }>;
}

export async function preflightAdText(text: string) {
  return api<PreflightResponse>("/api/pre-flight/validate", {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

// ─── Helpers (UI labels) ────────────────────────────────────────────────

export const STATUS_META: Record<HypothesisStatus, { label: string; color: "amber" | "blue" | "green" | "purple" | "red"; description: string }> = {
  draft: { label: "Черновик", color: "amber", description: "Заполняется, ещё не отправлена на ревью" },
  pending_review: { label: "На ревью", color: "blue", description: "Ждёт согласования перед approve" },
  approved: { label: "Одобрена", color: "purple", description: "Готова к запуску в Я.Директ" },
  active: { label: "Активна", color: "green", description: "Кампания работает, набирает данные" },
  paused: { label: "Пауза", color: "amber", description: "Suspend в Я.Директе, можно возобновить" },
  verdict_pending: { label: "Ждёт verdict", color: "blue", description: "Накопила данных, нужен verdict" },
  killed: { label: "Убита", color: "red", description: "Финальный статус — кампания не подтвердилась" },
  scaled: { label: "Масштабируется", color: "green", description: "Финальный статус — кампания работает" },
};
